import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Loader2, Minus, Plus, Printer, RefreshCw, Truck } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { groupByStore } from "@/lib/shipping/group-credentials";
import { MAX_PACKAGES, clampPackageCount, parsePackageCount } from "@/lib/shipping/packages";
import { useActiveShippingCredentials } from "@/hooks/use-shipping";
import { WHOLESALE_STORE_ID } from "@/lib/wholesale";
import { openBlob } from "@/lib/utils";
import type { ImportResult } from "@/services/shipping";

const LAST_CREDENTIAL_KEY = "komoran:wholesaleCourierCredential";
const PENDING_DEFAULT_MESSAGE =
  "Prethodni pokušaj zaglavio (timeout) — provjerite portal kurira i ručno unesite tracking, ili obrišite pošiljku za ponovno slanje";

function readLastCredential(): string {
  try { return localStorage.getItem(LAST_CREDENTIAL_KEY) ?? ""; } catch { return ""; }
}
function writeLastCredential(id: string) {
  try { localStorage.setItem(LAST_CREDENTIAL_KEY, id); } catch { /* privatni prozor — samo se ne pamti */ }
}

/** Safari (i svaki pregledač na iOS-u, svi su WebKit) često printa praznu stranicu za PDF
 *  u iframeu, bez ikakve greške. */
const IS_SAFARI = typeof navigator !== "undefined" && (
  /iphone|ipad|ipod/i.test(navigator.userAgent)
  || (/safari/i.test(navigator.userAgent) && !/chrome|chromium|crios|fxios|edg|android/i.test(navigator.userAgent))
);

/** Zaglavljeni pokušaj kod kurira: nalog je možda napravljen, tracking nije stigao. */
type PendingInfo = { externalId?: string | null; credentialName?: string | null; message?: string };

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `local_id` veleprodajne narudžbe — ključ pod kojim je projektovana u mirror i pošiljke. */
  localId: string | number;
  orderNumber: string;
  /** Kad pošiljka već postoji, dialog odmah prikazuje naljepnicu (ponovno printanje). */
  trackingNumber?: string | null;
  /** Raniji pokušaj je istekao kod kurira — umjesto novog naloga nudi ručni unos trackinga. */
  pendingShipment?: { externalId?: string | null; credentialName?: string | null } | null;
  onCreated?: (trackingNumber: string) => void;
};

/** Pravi nalog u brzoj pošti za jednu veleprodajnu narudžbu (izbor naloga i broja paketa),
 *  pa u istom dialogu prikazuje naljepnicu spremnu za printanje. */
export function CourierShipmentDialog({ open, onOpenChange, localId, orderNumber, trackingNumber, pendingShipment, onCreated }: Props) {
  const qc = useQueryClient();
  const { data: credentials, isLoading: credLoading } = useActiveShippingCredentials(open && !trackingNumber);
  const [step, setStep] = useState<"form" | "label">(trackingNumber ? "label" : "form");
  const [credentialId, setCredentialId] = useState("");
  const [packagesInput, setPackagesInput] = useState("1");
  const [allowOpening, setAllowOpening] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdTracking, setCreatedTracking] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingInfo | null>(null);

  const packages = parsePackageCount(packagesInput);

  // Svako otvaranje kreće iznova: s naljepnicom ako pošiljka postoji, inače s formom
  // (ili s ručnim unosom trackinga ako je raniji pokušaj zaglavio).
  useEffect(() => {
    if (!open) return;
    setStep(trackingNumber ? "label" : "form");
    setPackagesInput("1");
    setAllowOpening(false);
    setError(null);
    setCreatedTracking(null);
    setPending(!trackingNumber && pendingShipment ? { ...pendingShipment } : null);
  }, [open]);

  // Zadnji korišteni nalog je prvi izbor — veleprodaja gotovo uvijek ide istim kurirom.
  useEffect(() => {
    if (!credentials?.length || credentialId) return;
    const last = readLastCredential();
    setCredentialId(credentials.some((c: any) => c.id === last) ? last : credentials.length === 1 ? credentials[0].id : "");
  }, [credentials, credentialId]);

  function showLabel(tracking: string) {
    setCreatedTracking(tracking || null);
    onCreated?.(tracking);
    // Namjerno bez osvježavanja `fulfillment-lookup`: Pack bi ponovo pitao za narudžbu koja je
    // upravo postala „poslana“ i dobio grešku statusa dok je naljepnica još otvorena.
    qc.invalidateQueries({ queryKey: ["wholesale"] });
    setPending(null);
    setStep("label");
  }

  function stepPackages(delta: number) {
    setPackagesInput((v) => String(clampPackageCount((parsePackageCount(v) ?? 1) + delta)));
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!credentialId || creating || packages === null) return;
    setCreating(true);
    setError(null);
    try {
      const res = await fetch("/api/shipping/import", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orderIds: [String(localId)],
          storeId: WHOLESALE_STORE_ID,
          credentialId,
          numberOfPackages: packages,
          allowOpening,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || "Greška pri kreiranju naloga");
      const result: ImportResult | undefined = body.results?.[0];
      writeLastCredential(credentialId);
      // Nalog je već napravljen ranije (npr. bulk uvozom) — naljepnica je i dalje ono što treba.
      if (result?.success || result?.code === "already_shipped") {
        showLabel(result.trackingNumber ?? "");
        return;
      }
      // Kurir nije odgovorio na vrijeme (sada ili ranije): novi nalog bi mogao biti duplikat,
      // pa ostaje samo ručni unos trackinga s portala kurira.
      if (result?.code === "pending" || result?.code === "timeout") {
        setPending({ message: result.error });
        return;
      }
      throw new Error(result?.error || "Kurir nije vratio tracking broj");
    } catch (err: any) {
      setError(err?.message || "Greška pri kreiranju naloga");
    } finally {
      setCreating(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!creating) onOpenChange(o); }}>
      <DialogContent
        className={step === "label" ? "sm:max-w-2xl" : "sm:max-w-sm"}
        onInteractOutside={(e) => { if (creating) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>{step === "label" ? "Naljepnica" : "Nalog u brzoj pošti"}</DialogTitle>
          <DialogDescription>
            {step === "label"
              ? `Narudžba ${orderNumber}${createdTracking || trackingNumber ? ` · pošiljka ${createdTracking || trackingNumber}` : ""}`
              : `Narudžba ${orderNumber}`}
          </DialogDescription>
        </DialogHeader>

        {step === "label" ? (
          <LabelPreview localId={String(localId)} onClose={() => onOpenChange(false)} />
        ) : pending ? (
          <ManualTracking
            localId={String(localId)}
            pending={pending}
            busy={creating}
            onBusyChange={setCreating}
            onAttached={showLabel}
          />
        ) : credLoading ? (
          <p className="text-xs text-muted-foreground">Učitavanje...</p>
        ) : !credentials?.length ? (
          <div className="bg-yellow-500/10 border border-yellow-500/20 p-3 text-xs text-yellow-700">
            Nema aktivnih kurirskih naloga. Dodajte nalog u Postavkama.
          </div>
        ) : (
          <form onSubmit={handleCreate} className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label>Kurirska služba</Label>
              <Select value={credentialId} onValueChange={setCredentialId} disabled={creating}>
                <SelectTrigger className="w-full"><SelectValue placeholder="Odaberi" /></SelectTrigger>
                <SelectContent>
                  {groupByStore(credentials).map(([storeName, group]) => (
                    <SelectGroup key={storeName || "—"}>
                      <SelectLabel>{storeName || "Bez trgovine"}</SelectLabel>
                      {group.map((c: any) => (
                        <SelectItem key={c.id} value={c.id}>
                          <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 shrink-0" style={{ backgroundColor: c.color }} />
                            {c.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="courier-packages">Broj paketa</Label>
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" size="icon" aria-label="Manje paketa"
                  disabled={creating || (packages ?? 1) <= 1}
                  onClick={() => stepPackages(-1)}>
                  <Minus className="h-4 w-4" />
                </Button>
                <Input
                  id="courier-packages"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={packagesInput}
                  disabled={creating}
                  aria-invalid={packages === null}
                  // Unos ostaje tekst dok se kuca (prazno pa „3“ je 3, ne 13); ispravlja se tek na blur.
                  onChange={(e) => setPackagesInput(e.target.value.replace(/\D/g, "").slice(0, 3))}
                  onBlur={() => setPackagesInput((v) => String(clampPackageCount(Number(v) || 1)))}
                  className="h-9 text-center text-base font-medium tabular-nums"
                />
                <Button type="button" variant="outline" size="icon" aria-label="Više paketa"
                  disabled={creating || (packages ?? 0) >= MAX_PACKAGES}
                  onClick={() => stepPackages(1)}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {packages === null && (
                <p className="text-xs text-destructive">Broj paketa mora biti od 1 do {MAX_PACKAGES}.</p>
              )}
            </div>

            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <Checkbox checked={allowOpening} disabled={creating} onCheckedChange={(v) => setAllowOpening(!!v)} />
              Dozvoli otvaranje pošiljke
            </label>

            {error && <p className="text-xs text-destructive">{error}</p>}

            <DialogFooter>
              <Button type="submit" disabled={!credentialId || creating || packages === null} className="gap-1.5">
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Truck className="h-4 w-4" />}
                {creating ? "Kreiranje..." : "Napravi nalog"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Raniji pokušaj je istekao kod kurira: nalog možda postoji na portalu kurira, pa se novi
 *  ne pravi (bio bi duplikat) — tracking s portala se upisuje ručno i pošiljka postaje kreirana. */
function ManualTracking({ localId, pending, busy, onBusyChange, onAttached }: {
  localId: string;
  pending: PendingInfo;
  busy: boolean;
  onBusyChange: (busy: boolean) => void;
  onAttached: (trackingNumber: string) => void;
}) {
  const [tracking, setTracking] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const value = tracking.trim();
    if (!value || busy) return;
    onBusyChange(true);
    setError(null);
    try {
      const res = await fetch("/api/shipping/shipments/manual-tracking", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderId: localId, storeId: WHOLESALE_STORE_ID, trackingNumber: value }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || "Greška pri upisu trackinga");
      onAttached(body.trackingNumber || value);
    } catch (err: any) {
      setError(err?.message || "Greška pri upisu trackinga");
    } finally {
      onBusyChange(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      <div className="bg-yellow-500/10 border border-yellow-500/20 p-3 text-xs text-yellow-700 flex flex-col gap-1">
        <p>{pending.message || PENDING_DEFAULT_MESSAGE}</p>
        {(pending.credentialName || pending.externalId) && (
          <p className="tabular-nums">
            {[pending.credentialName, pending.externalId && `externalId: ${pending.externalId}`].filter(Boolean).join(" · ")}
          </p>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="courier-manual-tracking">Tracking broj s portala kurira</Label>
        <Input
          id="courier-manual-tracking"
          value={tracking}
          disabled={busy}
          autoComplete="off"
          onChange={(e) => setTracking(e.target.value)}
          className="h-9 tabular-nums"
        />
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      <DialogFooter>
        <Button type="submit" disabled={!tracking.trim() || busy} className="gap-1.5">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Truck className="h-4 w-4" />}
          {busy ? "Spremanje..." : "Sačuvaj tracking"}
        </Button>
      </DialogFooter>
    </form>
  );
}

/** Naljepnica iz istog generatora kao maloprodajna, prikazana u dialogu da se printa bez
 *  traženja novog taba. */
function LabelPreview({ localId, onClose }: { localId: string; onClose: () => void }) {
  const [url, setUrl] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const frameRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    setUrl(null);
    setBlob(null);
    setError(null);
    setLoaded(false);
    (async () => {
      try {
        const res = await fetch("/api/fulfillment/labels", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ orderIds: [localId], storeId: WHOLESALE_STORE_ID }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || "Greška pri generisanju naljepnice");
        }
        const pdf = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(pdf);
        setBlob(pdf);
        setUrl(objectUrl);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || "Greška pri generisanju naljepnice");
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [localId, attempt]);

  // Neki pregledači (i headless Chrome bez PDF preglednika) ne okinu `load` na iframeu
  // s PDF-om — Printaj tad ne smije ostati zauvijek onemogućen.
  useEffect(() => {
    if (!url) return;
    const t = setTimeout(() => setLoaded(true), 2000);
    return () => clearTimeout(t);
  }, [url]);

  function handlePrint() {
    // Safari iz iframea printa praznu stranicu bez greške — tamo PDF ide u novi tab.
    if (IS_SAFARI) {
      if (blob) openBlob(blob, "naljepnica.pdf");
      return;
    }
    const win = frameRef.current?.contentWindow;
    if (!win) return;
    try {
      win.focus();
      win.print();
    } catch {
      // Neki pregledači ne daju printati PDF iz iframea — onda u novom tabu.
      if (blob) openBlob(blob, "naljepnica.pdf");
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="h-[60vh] ring-1 ring-foreground/10 bg-muted/30 flex items-center justify-center">
        {error ? (
          <div className="flex flex-col items-center gap-3 px-4 text-center">
            <p className="text-xs text-destructive">{error}</p>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setAttempt((n) => n + 1)}>
              <RefreshCw className="h-3.5 w-3.5" />
              Pokušaj ponovo
            </Button>
          </div>
        ) : url ? (
          <iframe ref={frameRef} src={url} title="Naljepnica" className="w-full h-full" onLoad={() => setLoaded(true)} />
        ) : (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        )}
      </div>
      <DialogFooter className="gap-2">
        <Button variant="outline" className="gap-1.5" disabled={!blob} onClick={() => blob && openBlob(blob, "naljepnica.pdf")}>
          <ExternalLink className="h-4 w-4" />
          Novi tab
        </Button>
        <Button variant="outline" onClick={onClose}>Zatvori</Button>
        <Button className="gap-1.5" disabled={IS_SAFARI ? !blob : !loaded} onClick={handlePrint}>
          <Printer className="h-4 w-4" />
          Printaj
        </Button>
      </DialogFooter>
    </div>
  );
}
